import { useState, useEffect } from 'react';
import authHeader from '../services/auth-header';

type PhotoState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; objectUrl: string }
  | { status: 'error' };

/**
 * Module-level cache (Option B): keyed by proxy URL → blob object URL.
 * Blob URLs stay alive for the lifetime of the page session.
 * Call clearPhotoCache() on logout to release memory and revoke blob URLs.
 */
const photoCache = new Map<string, string>();

export function clearPhotoCache(): void {
  photoCache.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
  photoCache.clear();
}

/**
 * Fetches a photo URL with the JWT Authorization header and returns a local
 * blob: object URL safe to use in <img src>.
 *
 * - Google Places proxy URLs (/places/photo) are fetched with auth headers.
 * - S3 / direct URLs are passed straight through without a fetch.
 * - Results are cached in a module-level Map to avoid redundant requests.
 */
export function useAuthenticatedPhoto(
  proxyUrl: string | null | undefined
): PhotoState {
  const [state, setState] = useState<PhotoState>(() => {
    if (!proxyUrl) return { status: 'idle' };
    // S3 / direct URLs need no fetch
    if (!proxyUrl.includes('/places/photo')) {
      return { status: 'loaded', objectUrl: proxyUrl };
    }
    // Already cached
    const cached = photoCache.get(proxyUrl);
    if (cached) return { status: 'loaded', objectUrl: cached };
    return { status: 'loading' };
  });

  useEffect(() => {
    if (!proxyUrl) {
      setState({ status: 'idle' });
      return;
    }

    // S3 / direct URLs — no auth needed
    if (!proxyUrl.includes('/places/photo')) {
      setState({ status: 'loaded', objectUrl: proxyUrl });
      return;
    }

    // Return cached result immediately
    const cached = photoCache.get(proxyUrl);
    if (cached) {
      setState({ status: 'loaded', objectUrl: cached });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    fetch(proxyUrl, { headers: authHeader() })
      .then((res) => {
        console.log(
          `[useAuthenticatedPhoto] fetch ${proxyUrl} → ${res.status} ${res.statusText}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        console.log(
          `[useAuthenticatedPhoto] blob created for ${proxyUrl} → ${objectUrl}`
        );
        photoCache.set(proxyUrl, objectUrl);
        setState({ status: 'loaded', objectUrl });
      })
      .catch((err) => {
        console.error(`[useAuthenticatedPhoto] FAILED ${proxyUrl}:`, err);
        if (!cancelled) setState({ status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [proxyUrl]);

  return state;
}
